import React, { memo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { ISMEDesignGuidelinesExtended, Column, DropdownOption } from './SMEDesignGuidelineTypes';
import { getDynamicStyles, getTableCellStyle, getRowStyle, getPerformanceMetricsStyles, getButtonStyles, getDialogStyles } from './SMEDesignGuidelineStyles';
import { getFormattedDate } from '../../../Utils/UnitsHelper';
import { FilterToggle } from '../../Atom/Filtering/FilterToggle';
import { SMEDesignGuidelineFilter, ISMEDesignGuidelineFilter } from './SMEDesignGuidelineFilter';

/**
 * Performance Metrics Display Component
 */
interface PerformanceMetricsProps {
  performanceMetrics: Record<string, number>;
}

export const PerformanceMetricsDisplay: React.FC<PerformanceMetricsProps> = memo(({ performanceMetrics }) => {
  const theme = useTheme();
  const styles = getPerformanceMetricsStyles(theme);
  
  if (process.env.NODE_ENV !== 'development' || Object.keys(performanceMetrics).length === 0) {
    return null;
  }

  return (
    <Paper sx={styles.container}>
      <Typography variant="h6">Performance Metrics</Typography>
      <Box
        component="pre"
        sx={styles.content.sx}
      >
        {Object.entries(performanceMetrics)
          .sort(([, a], [, b]) => b - a)
          .map(([key, value]) => `${key}: ${value.toFixed(2)}ms`)
          .join('\n')}
      </Box>
    </Paper>
  );
});
PerformanceMetricsDisplay.displayName = 'PerformanceMetricsDisplay';

/**
 * Action Buttons Header Component
 */
interface ActionButtonsHeaderProps {
  onCreateNew: () => void;
  isLoading: boolean;
  isFiltering: boolean;
  showFilters: boolean;
  filteredDataLength: number;
  onToggleFilters: () => void;
  onResetFilters: () => void;
}

export const ActionButtonsHeader: React.FC<ActionButtonsHeaderProps> = memo(({
  onCreateNew,
  isLoading,
  isFiltering,
  showFilters,
  filteredDataLength,
  onToggleFilters,
  onResetFilters
}) => {
  const theme = useTheme();
  const styles = getDynamicStyles(theme);
  const buttonStyles = getButtonStyles();

  return (
    <Box sx={styles.actionButtonsContainer}>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={onCreateNew}
        disabled={isLoading}
        sx={buttonStyles.createButton}
      >
        Create new design guideline
      </Button>
      
      <FilterToggle 
        backPath=''
        isFiltering={isFiltering}
        showFilters={showFilters}
        isLoading={isLoading}
        toggleShowFilters={onToggleFilters}
        onResetFilters={onResetFilters}
        resultCount={filteredDataLength}
      />
    </Box>
  );
});
ActionButtonsHeader.displayName = 'ActionButtonsHeader';

/**
 * Filter Container Component
 */
interface FilterContainerProps {
  showFilters: boolean;
  filter: ISMEDesignGuidelineFilter;
  isFiltering: boolean;
  filteredData: ISMEDesignGuidelinesExtended[];
  currentData: ISMEDesignGuidelinesExtended[];
  onFilterChange: (filter: ISMEDesignGuidelineFilter, isFiltering: boolean) => void;
}

export const FilterContainer: React.FC<FilterContainerProps> = memo(({
  showFilters,
  filter,
  isFiltering,
  filteredData,
  currentData,
  onFilterChange
}) => {
  const theme = useTheme();
  const styles = getDynamicStyles(theme);

  if (!showFilters) return null;

  return (
    <Paper elevation={0} sx={styles.filterContainer}>
      <SMEDesignGuidelineFilter 
        onChange={onFilterChange}
        filter={filter}
        isFiltering={isFiltering}
        filteredData={filteredData}
        currentData={currentData}
      />
    </Paper>
  );
});
FilterContainer.displayName = 'FilterContainer';

/**
 * Loading Component
 */
export const LoadingDisplay: React.FC = memo(() => {
  const theme = useTheme();
  const styles = getDynamicStyles(theme);

  return (
    <Box sx={styles.loadingContainer}>
      <CircularProgress size={40} />
      <Typography variant="body1" ml={2}>
        Loading...
      </Typography>
    </Box>
  );
});
LoadingDisplay.displayName = 'LoadingDisplay';

/**
 * No Results Component
 */
export const NoResultsDisplay: React.FC = memo(() => {
  const theme = useTheme();
  const styles = getDynamicStyles(theme);

  return (
    <Paper sx={styles.noResultsMessage}>
      <Typography variant="body1" color="text.secondary">
        No design guidelines match the specified filters
      </Typography>
    </Paper>
  );
});
NoResultsDisplay.displayName = 'NoResultsDisplay';

/**
 * Memoized Table Row Component
 */
interface MemoizedTableRowProps {
  item: ISMEDesignGuidelinesExtended;
  columns: Column[];
  onEdit: (item: ISMEDesignGuidelinesExtended) => void;
}

export const MemoizedTableRow: React.FC<MemoizedTableRowProps> = memo(({ item, columns, onEdit }) => {
  const theme = useTheme();
  
  return (
    <TableRow 
      hover
      sx={getRowStyle(theme, item)}
    >
      {columns.map((column) => (
        <TableCell 
          key={column.id} 
          align={column.align || 'left'}
          style={getTableCellStyle(column)}
        >
          {column.format 
            ? column.format(item[column.id as keyof ISMEDesignGuidelinesExtended], item) 
            : String(item[column.id as keyof ISMEDesignGuidelinesExtended] ?? '')}
        </TableCell>
      ))}
    </TableRow>
  );
});
MemoizedTableRow.displayName = 'MemoizedTableRow';

/**
 * Data Table Component
 */
interface DataTableProps {
  filteredData: ISMEDesignGuidelinesExtended[];
  sortedData: ISMEDesignGuidelinesExtended[];
  dataColumns: Column[];
  order: 'asc' | 'desc';
  orderBy: keyof ISMEDesignGuidelinesExtended | '';
  onEdit: (item: ISMEDesignGuidelinesExtended) => void;
  onRequestSort: (property: keyof ISMEDesignGuidelinesExtended) => void;
}

export const DataTable: React.FC<DataTableProps> = memo(({
  filteredData,
  sortedData,
  dataColumns,
  order,
  orderBy,
  onEdit,
  onRequestSort
}) => {
  const theme = useTheme();
  const styles = getDynamicStyles(theme);

  if (filteredData.length === 0) {
    return <NoResultsDisplay />;
  }

  return (
    <TableContainer component={Paper} sx={styles.tableContainer}>
      <Table aria-label="SME design guidelines table" sx={styles.table}>
        <TableHead>
          <TableRow>
            {dataColumns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align || 'left'}
                style={{ 
                  width: column.minWidth,
                  maxWidth: column.maxWidth,
                }}
                sx={styles.headerCell}
                sortDirection={orderBy === column.id ? order : false}
              >
                {column.sortable !== false ? (
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : 'asc'}
                    onClick={() => onRequestSort(column.id as keyof ISMEDesignGuidelinesExtended)}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  <Typography variant="body2" fontWeight="bold">
                    {column.label}
                  </Typography>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.map((item, index) => (
            <MemoizedTableRow 
              key={`${item.industry}-${item.subIndustry}-${item.application}-${index}`}
              item={item}
              columns={dataColumns}
              onEdit={onEdit}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
});
DataTable.displayName = 'DataTable';

/**
 * Form Dialog Component
 */
interface FormDialogProps {
  isOpen: boolean;
  title: string;
  isDisabled: boolean;
  industries: DropdownOption[];
  subIndustries: DropdownOption[];
  applications: DropdownOption[];
  subApplications: DropdownOption[];
  productLines: DropdownOption[];
  processStages: DropdownOption[];
  alfaLavalPositions: DropdownOption[];
  selectedIndustry: string;
  selectedSubIndustry: string;
  selectedApplication: string;
  selectedSubApplication: string;
  selectedProductLine: string;
  selectedProcessStage: string;
  selectedPosition: string;
  designSuggestion: string;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onSelectChange: (event: any, field: string) => void;
  onDesignSuggestionChange: (value: string) => void;
}

export const FormDialog: React.FC<FormDialogProps> = memo(({
  isOpen,
  title,
  isDisabled,
  industries,
  subIndustries,
  applications,
  subApplications,
  productLines,
  processStages,
  alfaLavalPositions,
  selectedIndustry,
  selectedSubIndustry,
  selectedApplication,
  selectedSubApplication,
  selectedProductLine,
  selectedProcessStage,
  selectedPosition,
  designSuggestion,
  onClose,
  onSubmit,
  onSelectChange,
  onDesignSuggestionChange
}) => {
  const theme = useTheme();
  const dialogStyles = getDialogStyles(theme);
  const styles = getDynamicStyles(theme);

  return (
    <Dialog 
      open={isOpen} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: dialogStyles.paper
      }}
    >
      <DialogTitle sx={dialogStyles.title}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
        <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <form onSubmit={onSubmit}>
        <DialogContent>
          <Stack spacing={3}>
            <Box sx={dialogStyles.formContainer}>
              <FormControl fullWidth sx={styles.formControl}>
                <InputLabel id="industry-label">Industry</InputLabel>
                <Select
                  labelId="industry-label"
                  value={selectedIndustry}
                  onChange={(e) => onSelectChange(e, 'industry')}
                  label="Industry"
                  disabled={isDisabled}
                  required
                >
                  {industries.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl fullWidth sx={styles.formControl}>
                <InputLabel id="subIndustry-label">Sub Industry</InputLabel>
                <Select
                  labelId="subIndustry-label"
                  value={selectedSubIndustry}
                  onChange={(e) => onSelectChange(e, 'subIndustry')}
                  label="Sub Industry"
                  disabled={isDisabled}
                  required
                >
                  {subIndustries.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={dialogStyles.formContainer}>
              <FormControl fullWidth sx={styles.formControl}>
                <InputLabel id="application-label">Application</InputLabel>
                <Select
                  labelId="application-label"
                  value={selectedApplication}
                  onChange={(e) => onSelectChange(e, 'application')}
                  label="Application"
                  disabled={isDisabled}
                  required
                >
                  {applications.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl fullWidth sx={styles.formControl}>
                <InputLabel id="subApplication-label">Sub Application</InputLabel>
                <Select
                  labelId="subApplication-label"
                  value={selectedSubApplication}
                  onChange={(e) => onSelectChange(e, 'subApplication')}
                  label="Sub Application"
                  disabled={isDisabled}
                  required
                >
                  {subApplications.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={dialogStyles.formContainer}>
              <FormControl fullWidth sx={styles.formControl}>
                <InputLabel id="processStage-label">Process Stage</InputLabel>
                <Select
                  labelId="processStage-label"
                  value={selectedProcessStage}
                  onChange={(e) => onSelectChange(e, 'processStage')}
                  label="Process Stage"
                  disabled={isDisabled}
                  required
                >
                  {processStages.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl fullWidth sx={styles.formControl}>
                <InputLabel id="productLine-label">Product Line</InputLabel>
                <Select
                  labelId="productLine-label"
                  value={selectedProductLine}
                  onChange={(e) => onSelectChange(e, 'productLine')}
                  label="Product Line"
                  disabled={isDisabled}
                  required
                >
                  {productLines.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={dialogStyles.formContainer}>
              <FormControl fullWidth sx={styles.formControl}>
                <InputLabel id="position-label">Alfa Laval Position</InputLabel>
                <Select
                  labelId="position-label"
                  value={selectedPosition}
                  onChange={(e) => onSelectChange(e, 'position')}
                  label="Alfa Laval Position"
                  disabled={isDisabled}
                  required
                >
                  {alfaLavalPositions.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <TextField
              label="Design Suggestion"
              multiline
              rows={4}
              value={designSuggestion}
              onChange={(e) => onDesignSuggestionChange(e.target.value)}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
          </Stack>
        </DialogContent>
        
        <DialogActions sx={dialogStyles.actions}>
          <Button 
            onClick={onClose} 
            color="inherit"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            startIcon={<CheckIcon />}
          >
            Submit
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
});
FormDialog.displayName = 'FormDialog';

/**
 * Create column format functions for table columns
 */
export const createColumnFormats = () => ({
  tooltipFormat: (value: any) => (
    <Tooltip title={value}>
      <Typography variant="body2" noWrap>
        {value}
      </Typography>
    </Tooltip>
  ),
  
  dateFormat: (value: any) => {
    if (!value) return null;
    const formattedDate = getFormattedDate(value.toString());
    return (
      <Tooltip title={formattedDate}>
        <Typography variant="body2" noWrap>
          {formattedDate}
        </Typography>
      </Tooltip>
    );
  },
  
  versionFormat: (value: any) => (
    <Tooltip title={`Version: ${value}`}>
      <Typography variant="body2" noWrap>
        {value}
      </Typography>
    </Tooltip>
  ),
  
  actionsFormat: (value: any, item?: ISMEDesignGuidelinesExtended, onEdit?: (item: ISMEDesignGuidelinesExtended) => void) => (
    <>
      {item?.isEditable && item?.canEdit ? (
        <IconButton
          color="primary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            if (onEdit && item) onEdit(item);
          }}
        >
          <EditIcon />
        </IconButton>
      ) : (
        <Tooltip title="This guideline is used in active blind test or data sample, cannot be edited">
          <span>
            <IconButton
              color="primary"
              size="small"
              disabled
            >
              <EditIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </>
  )
});

export const PlaceholderComponent: React.FC = memo(() => {
  return <div>Placeholder</div>;
});
PlaceholderComponent.displayName = 'PlaceholderComponent'; 